import { Link } from "react-router-dom";
import { ArrowRight, Building2, Sprout, Network, Briefcase } from "lucide-react";
import { useSeo } from "@/lib/seo";
import Reveal from "@/components/site/Reveal";
import { industries } from "@/data/services";

const INDUSTRY_IMG =
  "https://images.unsplash.com/photo-1753351052617-62818ffc9173?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwyfHxzbWFsbCUyMGJ1c2luZXNzJTIwb3duZXIlMjBzdG9yZXxlbnwwfHx8fDE3ODIzOTg3MDZ8MA&ixlib=rb-4.1.0&q=85";

const icons = [Sprout, Briefcase, Network, Building2];

export default function Industries() {
  useSeo({
    title: "Industries Served",
    description:
      "We support new businesses, small businesses without dedicated HR, newly unionized organizations, and medium-sized businesses across Ontario.",
    path: "/industries",
  });

  return (
    <div data-testid="page-industries">
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="tpc-eyebrow">Industries served</p>
              <h1 className="mt-5 font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight">
                Wherever you are in the{" "}
                <span className="italic text-[#82917B]">growth curve</span>, we
                meet you there.
              </h1>
              <p className="mt-7 text-lg text-[#4A5348] leading-relaxed max-w-2xl">
                Our partners look different on paper, but they share something
                important: they care about getting the people work right.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.1}>
              <div className="relative">
                <div className="absolute -inset-3 bg-[#F3EFE7] -z-10" />
                <img
                  src={INDUSTRY_IMG}
                  alt="A small business owner welcoming customers into their storefront"
                  className="w-full h-[420px] object-cover"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 space-y-6">
          {industries.map((ind, i) => {
            const Icon = icons[i % icons.length];
            return (
              <Reveal key={ind.slug} delay={i * 0.06}>
                <article
                  data-testid={`industry-${ind.slug}`}
                  className="grid grid-cols-1 md:grid-cols-12 gap-6 p-8 md:p-10 border border-[#82917B]/20 bg-[#FDFBF7] hover:border-[#82917B]/40 transition-colors"
                >
                  <div className="md:col-span-2">
                    <div className="h-14 w-14 bg-[#F3EFE7] flex items-center justify-center text-[#82917B]">
                      <Icon size={24} />
                    </div>
                    <p className="mt-4 tpc-eyebrow">0{i + 1}</p>
                  </div>
                  <div className="md:col-span-7">
                    <h2 className="font-serif text-3xl md:text-4xl tracking-tight text-[#2B3329]">
                      {ind.title}
                    </h2>
                    <p className="mt-4 text-[#4A5348] text-[16px] leading-relaxed">
                      {ind.description}
                    </p>
                  </div>
                  <div className="md:col-span-3 flex md:justify-end items-end">
                    <Link
                      to="/contact"
                      data-testid={`industry-cta-${ind.slug}`}
                      className="tpc-link"
                    >
                      Talk to us <ArrowRight size={16} />
                    </Link>
                  </div>
                </article>
              </Reveal>
            );
          })}
        </div>
      </section>

      <section className="py-20 md:py-28 bg-[#2B3329] text-[#E9E5DC]">
        <div className="max-w-5xl mx-auto px-6 lg:px-12 text-center">
          <Reveal>
            <h2 className="font-serif text-4xl sm:text-5xl tracking-tight text-[#FDFBF7]">
              Not sure if we’re the right fit?
            </h2>
            <p className="mt-5 text-[#C9CCC1] text-lg max-w-2xl mx-auto">
              Tell us about your team and where you’re stuck. We’ll be candid
              about whether we can help — and refer you on if we can’t.
            </p>
            <Link
              to="/contact"
              data-testid="industries-cta-book"
              className="tpc-btn tpc-btn-sage mt-8"
            >
              Book a Consultation <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
